import React, { useState } from "react";

export default function PlayersList({
  players = [],
  roundHistory = [],
  potyStandings = [],
  onSelectPlayer,
}) {
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  // 1. Filter for Members only
  const members = players.filter((p) => p.is_member);

  // 2. Identify Leader Points for "Points Behind" calculation
  const leaderPoints = potyStandings.length > 0 ? potyStandings[0].points : 0;

  const getPlayerStats = (player) => {
    const name = player.full_name || player.p_name;

    // Get POTY data from the existing standings you passed in
    const potyEntry = potyStandings.find((s) => s.name === name);
    const rank = potyStandings.findIndex((s) => s.name === name) + 1;

    // HCP TREND calculation (Comparing current to the very last round they played)
    const lastRound = [...roundHistory]
      .reverse()
      .find((r) => r.players?.includes(name));
    const previousHcp = lastRound?.player_handicaps?.[name];
    const currentHcp =
      player.calculated_handicap ?? player.initial_handicap ?? 0;
    const change = previousHcp !== undefined ? currentHcp - previousHcp : 0;

    return {
      rank: rank > 0 ? rank : "-",
      ptsBehind: potyEntry
        ? (potyEntry.points - leaderPoints).toFixed(1)
        : "0.0",
      hcp: currentHcp,
      change: change,
    };
  };

  const sortedMembers = [...members].sort((a, b) =>
    (a.full_name || "").localeCompare(b.full_name || "")
  );

  return (
    <>
    <div
      className="screen"
      style={{ paddingBottom: "100px", backgroundColor: "#f4f7f6" }}
    >
      {/* HEADER */}
      <div
        style={{
          background: "#1b4332",
          padding: "20px",
          color: "white",
          textAlign: "center",
          marginBottom: "10px",
        }}
      >
        <h2
          style={{
            margin: 0,
            fontSize: "1.3rem",
            fontWeight: "800",
            letterSpacing: "0.5px",
          }}
        >
          Member Directory
        </h2>
      </div>

      {/* COLUMN HEADINGS */}
      <div
        style={{
          display: "flex",
          padding: "0 25px 5px 25px",
          fontSize: "0.65rem",
          fontWeight: "800",
          color: "#888",
          textTransform: "uppercase",
        }}
      >
        <div style={{ flex: 1 }}>Member Name</div>
        <div style={{ width: "45px", textAlign: "center" }}>HCP</div>
        <div style={{ width: "45px", textAlign: "center" }}>Trend</div>
      </div>

      <div style={{ padding: "0 15px" }}>
        {[...sortedMembers]
          .sort((a, b) => getPlayerStats(a).rank - getPlayerStats(b).rank) // <-- ADD THIS LINE
          .map((player) => {
            const stats = getPlayerStats(player);
            const isLeader = stats.ptsBehind === "0.0" && stats.rank === 1;

            return (
              <div
                key={player.id}
                onClick={() => setSelectedPlayer(player)}
                style={{
                  background: "white",
                  marginBottom: "8px",
                  padding: "12px 15px",
                  borderRadius: "10px",
                  display: "flex",
                  alignItems: "center",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.04)",
                  border: "1px solid #eee",
                }}
              >
                {/* RANK NUMBER */}
                <div
                  style={{
                    width: "25px",
                    fontWeight: "bold",
                    color: "#aaa",
                    fontSize: "0.9rem",
                  }}
                >
                  {stats.rank}
                </div>

                {/* NAME & POTY INFO */}
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontWeight: "800",
                      color: "#1b4332",
                      fontSize: "1rem",
                    }}
                  >
                    {player.full_name}
                  </div>
                  <div
                    style={{
                      fontSize: "0.7rem",
                      color: isLeader ? "#2d6a4f" : "#666",
                      fontWeight: isLeader ? "bold" : "normal",
                    }}
                  >
                    {isLeader
                      ? "★ POTY Leader"
                      : `${stats.ptsBehind} pts behind`}
                  </div>
                </div>

                {/* STATS COLUMNS */}
                <div style={{ display: "flex", gap: "5px" }}>
                  <div style={{ width: "45px", textAlign: "center" }}>
                    <div
                      style={{
                        fontWeight: "800",
                        fontSize: "1.1rem",
                        color: "#333",
                      }}
                    >
                      {stats.hcp}
                    </div>
                  </div>
                  <div style={{ width: "45px", textAlign: "center" }}>
                    <div
                      style={{
                        fontWeight: "900",
                        fontSize: "0.9rem",
                        color:
                          stats.change < 0
                            ? "#2d6a4f"
                            : stats.change > 0
                            ? "#c1121f"
                            : "#aaa",
                      }}
                    >
                      {stats.change < 0 ? "↓" : stats.change > 0 ? "↑" : ""}
                      {Math.abs(stats.change)}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
      </div>
    </div>

    {/* PLAYER DETAIL MODAL */}
    {selectedPlayer && (() => {
      const pid = selectedPlayer.id;
      const currentHcp = selectedPlayer.calculated_handicap ?? selectedPlayer.initial_handicap ?? 0;

      // Rounds this player participated in
      const playerRounds = [...roundHistory]
        .filter(r => r.players?.includes(pid) || r.scores?.[pid])
        .sort((a, b) => new Date(a.date) - new Date(b.date));

      const playerRoundsNewest = [...playerRounds].reverse();

      // Handicap history for trend graph
      const hcpPoints = playerRounds
        .map(r => ({ date: r.date, hcp: r.player_handicaps?.[pid] }))
        .filter(p => p.hcp != null);

      // SVG graph constants
      const svgW = 300;
      const svgH = 180;
      const pL = 38; const pR = 12; const pT = 14; const pB = 14;
      const cW = svgW - pL - pR;
      const cH = svgH - pT - pB;

      const hcpValues = hcpPoints.map(p => p.hcp);
      const rawMin = Math.min(...hcpValues);
      const rawMax = Math.max(...hcpValues);
      const minH = rawMin === rawMax ? rawMin - 2 : rawMin;
      const maxH = rawMin === rawMax ? rawMax + 2 : rawMax;
      const range = maxH - minH || 1;

      const toX = (i) => pL + (hcpPoints.length > 1 ? (i / (hcpPoints.length - 1)) * cW : cW / 2);
      const toY = (h) => pT + (1 - (h - minH) / range) * cH;

      const polyPoints = hcpPoints.map((p, i) => `${toX(i)},${toY(p.hcp)}`).join(" ");

      const sumScores = (s) => {
        if (!s) return null;
        if (s.total != null) return Number(s.total);
        const vals = Object.values(s).map(Number).filter(v => v > 0);
        return vals.length > 0 ? vals.reduce((a, b) => a + b, 0) : null;
      };

      return (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          zIndex: 5000, background: "white", overflowY: "auto",
        }}>
          {/* Sticky header */}
          <div style={{
            position: "sticky", top: 0, zIndex: 1, background: "white",
            borderBottom: "1px solid #e2e8f0",
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "16px 20px",
          }}>
            <h2 style={{ margin: 0, color: "#1b4332", fontSize: "1.2rem", fontWeight: "800" }}>
              {selectedPlayer.full_name}
            </h2>
            <button
              onClick={() => setSelectedPlayer(null)}
              style={{ background: "none", border: "none", fontSize: "1.8rem", color: "#94a3b8", cursor: "pointer", lineHeight: 1 }}
            >
              ×
            </button>
          </div>

          {/* Current handicap */}
          <div style={{ textAlign: "center", padding: "24px 20px 12px" }}>
            <div style={{ fontSize: "3rem", fontWeight: "900", color: "#1b4332", lineHeight: 1 }}>
              {currentHcp}
            </div>
            <div style={{ fontSize: "0.65rem", fontWeight: "700", color: "#94a3b8", textTransform: "uppercase", marginTop: "6px", letterSpacing: "0.05em" }}>
              Current Handicap
            </div>
          </div>

          {/* Trend graph */}
          <div style={{ padding: "0 20px 20px" }}>
            {hcpPoints.length < 2 ? (
              <div style={{ textAlign: "center", color: "#94a3b8", fontSize: "0.85rem", padding: "20px 0" }}>
                Not enough data to show trend
              </div>
            ) : (
              <svg
                viewBox={`0 0 ${svgW} ${svgH}`}
                width="100%"
                height={svgH}
                style={{ display: "block" }}
              >
                {/* Y axis labels */}
                <text x={pL - 4} y={pT + 4} textAnchor="end" fontSize="10" fill="#94a3b8">{maxH.toFixed(1)}</text>
                <text x={pL - 4} y={pT + cH + 4} textAnchor="end" fontSize="10" fill="#94a3b8">{minH.toFixed(1)}</text>

                {/* Line */}
                <polyline
                  points={polyPoints}
                  fill="none"
                  stroke="#1b4332"
                  strokeWidth="2"
                  strokeLinejoin="round"
                />

                {/* Points */}
                {hcpPoints.map((p, i) => {
                  const isLast = i === hcpPoints.length - 1;
                  return (
                    <circle
                      key={i}
                      cx={toX(i)}
                      cy={toY(p.hcp)}
                      r={isLast ? 5 : 3}
                      fill={isLast ? "#10b981" : "#1b4332"}
                    />
                  );
                })}
              </svg>
            )}
          </div>

          {/* Round history */}
          <div style={{ borderTop: "1px solid #f1f5f9" }}>
            <div style={{ padding: "12px 20px 6px", fontSize: "0.7rem", fontWeight: "800", color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Round History
            </div>
            {playerRoundsNewest.length === 0 ? (
              <div style={{ padding: "16px 20px", color: "#94a3b8", fontSize: "0.85rem" }}>No rounds found.</div>
            ) : (
              playerRoundsNewest.map((r, i) => {
                const playerScores = r.scores?.[pid];
                const gross = sumScores(playerScores);
                const hcp = r.player_handicaps?.[pid] ?? currentHcp;
                const net = gross != null ? gross - hcp : null;
                const par = r.course_data?.holes?.reduce((sum, h) => sum + (h.par || 0), 0) || 72;
                const netVsPar = net != null ? net - par : null;

                const vsParLabel = netVsPar == null ? ""
                  : netVsPar === 0 ? "(E)"
                  : netVsPar < 0 ? `(${netVsPar})`
                  : `(+${netVsPar})`;
                const vsParColor = netVsPar == null ? "#94a3b8"
                  : netVsPar < 0 ? "#10b981"
                  : netVsPar > 0 ? "#ef4444"
                  : "#64748b";

                const dateLabel = new Date(r.date + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });
                const courseName = r.course_name || "Unknown";
                const courseShort = courseName.length > 12 ? courseName.slice(0, 12) + "…" : courseName;

                return (
                  <div key={r.id || i} style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    padding: "10px 20px", borderBottom: "1px solid #f8fafc",
                  }}>
                    <div style={{ fontSize: "0.8rem", color: "#64748b", width: "52px" }}>{dateLabel}</div>
                    <div style={{ fontSize: "0.8rem", color: "#334155", flex: 1, paddingLeft: "8px" }}>{courseShort}</div>
                    <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                      <span style={{ fontWeight: "800", fontSize: "0.9rem", color: "#1b4332" }}>
                        {gross ?? "—"}
                      </span>
                      <span style={{ fontSize: "0.8rem", fontWeight: "700", color: vsParColor, minWidth: "40px", textAlign: "right" }}>
                        {net != null ? net : "—"} {vsParLabel}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* View Full Stats button */}
          <div style={{ padding: "20px" }}>
            <button
              onClick={() => { onSelectPlayer(selectedPlayer); setSelectedPlayer(null); }}
              style={{
                width: "100%", padding: "16px",
                background: "#1b4332", color: "white",
                border: "none", borderRadius: "12px",
                fontWeight: "800", fontSize: "1rem", cursor: "pointer",
                boxShadow: "0 4px 12px rgba(27,67,50,0.2)",
              }}
            >
              View Full Stats
            </button>
          </div>
        </div>
      );
    })()}
    </>
  );
}
